// REACT & REACT HOOKS
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

// COMPONENTS
import { Col, Nav, Row, Tab } from "react-bootstrap";
import Loader from "components/shared/loader";
import { getNotificationType } from "components/notification/shared/notificationTypes";

// STORE ACTIONS
import {
    getNotifications,
    getMoreNotifications,
    markAllNotificationsAsRead,
} from "store/actions";

// ICONS & ASSETS
import styles from "./styles/style.module.scss";

const Index = () => {
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();
    const { notifications, metadata, loading } = useSelector(
        (state) => state.notifications
    );

    const unreadNotifications = notifications.filter(
        (notification) => !notification.read
    );
    const [pageAll, setPageAll] = useState(1);
    const [pageUnread, setPageUnread] = useState(1);

    const pageAllRef = useRef(pageAll);
    const pageUnreadRef = useRef(pageUnread);
    const loadingRef = useRef(loading);
    const metadataRef = useRef(metadata);

    useEffect(() => {
        pageAllRef.current = pageAll;
    }, [pageAll]);
    useEffect(() => {
        pageUnreadRef.current = pageUnread;
    }, [pageUnread]);
    useEffect(() => {
        loadingRef.current = loading;
    }, [loading]);
    useEffect(() => {
        metadataRef.current = metadata;
    }, [metadata]);

    useEffect(() => {
        dispatch(getNotifications());
    }, [dispatch]);

    // Create the observer only once
    const observerAll = useRef(null);
    const observerUnread = useRef(null);

    useEffect(() => {
        observerAll.current = new IntersectionObserver((entries) => {
            if (
                entries[0].isIntersecting &&
                metadataRef.current?.totalResults
            ) {
                console.log("Observer ALL triggered");
                if (
                    loadingRef.current ||
                    pageAllRef.current >=
                        Math.ceil(
                            metadataRef.current.totalResults /
                                metadataRef.current.pagination.limit
                        )
                )
                    return;
                const nextPage = pageAllRef.current + 1;
                dispatch(getMoreNotifications({ page: nextPage }));
                setPageAll(nextPage);
            }
        });
        return () => observerAll.current?.disconnect();
    }, [dispatch]);

    useEffect(() => {
        observerUnread.current = new IntersectionObserver((entries) => {
            if (
                entries[0].isIntersecting &&
                metadataRef.current?.totalResults
            ) {
                console.log("Observer UNREAD triggered");
                if (
                    loadingRef.current ||
                    pageUnreadRef.current >=
                        Math.ceil(
                            metadataRef.current.totalResults /
                                metadataRef.current.pagination.limit
                        )
                )
                    return;
                const nextPage = pageUnreadRef.current + 1;
                dispatch(getMoreNotifications({ page: nextPage }));
                setPageUnread(nextPage);
            }
        });
        return () => observerUnread.current?.disconnect();
    }, [dispatch]);

    return (
        <div className={styles["notifications-page"]}>
            <div className="notification-header">
                <h3>{formatMessage({ id: "notifications" })}</h3>
                {unreadNotifications.length > 0 && (
                    <button
                        type="button"
                        className="mark-all"
                        onClick={() => dispatch(markAllNotificationsAsRead())}
                    >
                        <FormattedMessage id="markAllAsRead" />
                    </button>
                )}
            </div>
            <Tab.Container defaultActiveKey="all">
                <Nav>
                    <Nav.Item>
                        <Nav.Link eventKey="all">
                            <FormattedMessage id="allNotifications" />{" "}
                            <i>
                                (
                                {metadata?.totalResults || notifications.length}
                                )
                            </i>
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="unread">
                            <FormattedMessage id="unreadNotifications" />{" "}
                            <i>({unreadNotifications.length})</i>
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
                <Tab.Content>
                    <Tab.Pane eventKey="all">
                        <Row>
                            {notifications.length > 0 ? (
                                notifications.map((notification, index) => {
                                    const notificationTemplate =
                                        getNotificationType(
                                            notification,
                                            index
                                        );
                                    if (!notificationTemplate?.Template)
                                        return null;
                                    const { Template, data } =
                                        notificationTemplate;
                                    return (
                                        <Col
                                            lg={12}
                                            xs={12}
                                            key={index}
                                            ref={
                                                index ===
                                                notifications.length - 1
                                                    ? (node) => {
                                                          if (node)
                                                              observerAll.current?.observe(
                                                                  node
                                                              );
                                                      }
                                                    : null
                                            }
                                        >
                                            <div className="notification-item">
                                                <Template
                                                    notification={notification}
                                                    {...data}
                                                />
                                            </div>
                                        </Col>
                                    );
                                })
                            ) : (
                                <div className="no-data">
                                    <FormattedMessage id="noNotifications" />
                                </div>
                            )}
                        </Row>
                        {loading && <Loader />}
                    </Tab.Pane>
                    <Tab.Pane eventKey="unread">
                        <Row>
                            {unreadNotifications.length > 0 ? (
                                unreadNotifications.map(
                                    (notification, index) => {
                                        const notificationTemplate =
                                            getNotificationType(
                                                notification,
                                                index
                                            );
                                        if (!notificationTemplate?.Template)
                                            return null;
                                        const { Template, data } =
                                            notificationTemplate;
                                        return (
                                            <Col
                                                lg={12}
                                                xs={12}
                                                key={index}
                                                ref={
                                                    index ===
                                                    unreadNotifications.length -
                                                        1
                                                        ? (node) => {
                                                              if (node)
                                                                  observerUnread.current?.observe(
                                                                      node
                                                                  );
                                                          }
                                                        : null
                                                }
                                            >
                                                <div className="notification-item">
                                                    <Template
                                                        notification={
                                                            notification
                                                        }
                                                        {...data}
                                                    />
                                                </div>
                                            </Col>
                                        );
                                    }
                                )
                            ) : (
                                <div className="no-data">
                                    <FormattedMessage id="noNotifications" />
                                </div>
                            )}
                        </Row>
                        {loading && <Loader />}
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>
        </div>
    );
};

export default Index;
